import { useMemo } from "react";
import { 
  Area, 
  AreaChart, 
  CartesianGrid, 
  Legend, 
  ResponsiveContainer, 
  Tooltip, 
  XAxis, 
  YAxis, 
  ReferenceLine 
} from "recharts";
import { TimePeriod, MetalPrice } from "@/lib/metal-price";

interface MetalAreaChartProps {
  chartData: any[];
  selectedTimePeriod: TimePeriod;
}

export function MetalAreaChart({ chartData, selectedTimePeriod }: MetalAreaChartProps) {
  const formatXAxis = (timestamp: number | string) => {
    if (!timestamp) return '';
    
    // Special case for 'all' period with year-only timestamps
    if (selectedTimePeriod === 'all') {
      // For 'all' period, if it's a year string, return it directly
      if (typeof timestamp === 'string' && /^\d{4}$/.test(timestamp)) {
        return timestamp;
      }
      
      // Otherwise try to format as a year
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.getFullYear().toString();
      }
    }
    
    // Handle case where timestamp is just a year (for other periods)
    if (typeof timestamp === 'string' && /^\d{4}$/.test(timestamp)) {
      return timestamp; // Just return the year as is
    }
    
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // If we can't parse it as a date, just return the raw value
      return String(timestamp);
    }
    
    switch (selectedTimePeriod) {
      case "week":
        return date.toLocaleDateString([], { weekday: 'short' });
      case "month":
        return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
      case "year":
        return date.toLocaleDateString([], { month: 'short' });
      default:
        return date.toLocaleDateString();
    }
  };

  const customTooltipFormatter = (value: number, name: string) => {
    const formattedValue = value.toFixed(2);
    const metal = name === 'gold' ? 'Oro' : 'Argento';
    return [`€${formattedValue}`, metal];
  };

  const averageValues = useMemo(() => {
    if (chartData.length === 0) return { gold: 0, silver: 0 };
    
    const goldSum = chartData.reduce((sum, item) => sum + item.gold, 0);
    const silverSum = chartData.reduce((sum, item) => sum + item.silver, 0);
    
    return {
      gold: goldSum / chartData.length,
      silver: silverSum / chartData.length
    };
  }, [chartData]);

  const goldYAxisDomain = useMemo(() => {
    if (chartData.length === 0) return ['dataMin - 5', 'dataMax + 5'];
    
    const goldValues = chartData.map(d => d.gold);
    const min = Math.min(...goldValues);
    const max = Math.max(...goldValues);
    const range = max - min;
    
    if (range < 2) {
      return [min - 2, max + 2];
    }
    
    return [min - range * 0.1, max + range * 0.1];
  }, [chartData]);

  const silverYAxisDomain = useMemo(() => {
    if (chartData.length === 0) return ['dataMin - 0.2', 'dataMax + 0.2'];
    
    const silverValues = chartData.map(d => d.silver);
    const min = Math.min(...silverValues);
    const max = Math.max(...silverValues);
    const range = max - min;
    
    if (range < 0.1) {
      return [min - 0.1, max + 0.1];
    }
    
    return [min - range * 0.1, max + range * 0.1];
  }, [chartData]);

  const goldReferenceLineProps = {
    yAxisId: "gold",
    y: averageValues.gold,
    stroke: "hsl(var(--gold))",
    strokeDasharray: "3 3",
    opacity: 0.6,
    label: {
      value: `Media: €${averageValues.gold.toFixed(2)}`,
      position: "insideBottomLeft" as const,
      fill: 'hsl(var(--gold))',
      fontSize: 11
    }
  };

  const silverReferenceLineProps = {
    yAxisId: "silver",
    y: averageValues.silver,
    stroke: "hsl(var(--silver))",
    strokeDasharray: "3 3",
    opacity: 0.6,
    label: {
      value: `Media: €${averageValues.silver.toFixed(2)}`,
      position: "insideBottomRight" as const,
      fill: 'hsl(var(--silver))',
      fontSize: 11
    }
  };

  // For 'all' period, we need to ensure the data is sorted by year
  const sortedChartData = useMemo(() => {
    if (selectedTimePeriod === 'all') {
      // First check if the data has year-only timestamps (for 'all' period)
      const hasYearOnlyTimestamps = chartData.some(item => 
        typeof item.timestamp === 'string' && /^\d{4}$/.test(item.timestamp)
      );
      
      if (hasYearOnlyTimestamps) {
        // If we have year-only timestamps, sort by year
        return [...chartData].sort((a, b) => {
          // Sort by year in ascending order
          const yearA = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : 0;
          const yearB = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : 0;
          return yearA - yearB;
        });
      } else {
        // If we don't have year-only timestamps (fallback to other period data),
        // we need to convert the timestamps to years for better visualization
        return [...chartData].map(item => {
          // Try to extract year from timestamp
          let timestamp = item.timestamp;
          if (typeof timestamp === 'string' && !(/^\d{4}$/.test(timestamp))) {
            try {
              const date = new Date(timestamp);
              if (!isNaN(date.getTime())) {
                // Use only the year as the timestamp for 'all' period
                timestamp = date.getFullYear().toString();
              }
            } catch (e) {
              // Keep original timestamp if parsing fails
            }
          }
          return { ...item, timestamp };
        }).sort((a, b) => {
          // Sort by year in ascending order
          const yearA = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : 0;
          const yearB = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : 0;
          return yearA - yearB;
        });
      }
    }
    return chartData;
  }, [chartData, selectedTimePeriod]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={sortedChartData}
        margin={{ top: 25, right: 35, left: 15, bottom: 15 }}
      >
        <defs>
          <linearGradient id="colorGold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--gold))" stopOpacity={0.6} />
            <stop offset="95%" stopColor="hsl(var(--gold))" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorSilver" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--silver))" stopOpacity={0.6} />
            <stop offset="95%" stopColor="hsl(var(--silver))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid 
          strokeDasharray="3 3" 
          vertical={false}
          opacity={0.15} 
          stroke="hsl(var(--muted-foreground))"
        />
        <XAxis
          dataKey="timestamp"
          tickFormatter={formatXAxis}
          tick={{ 
            fontSize: 12, 
            fill: "hsl(var(--foreground))",
            fontWeight: 'normal'
          }}
          stroke="hsl(var(--muted-foreground))"
          tickLine={{ stroke: "hsl(var(--muted-foreground))" }}
          axisLine={{ stroke: "hsl(var(--muted-foreground))" }}
          tickMargin={12}
          minTickGap={selectedTimePeriod === 'all' ? 0 : 0}
          interval={selectedTimePeriod === 'all' ? 0 : 'preserveStartEnd'}
          padding={{ left: 10, right: 10 }}
          allowDuplicatedCategory={false}
          angle={selectedTimePeriod === 'all' ? 0 : undefined}
          height={30}
        />
        <YAxis
          yAxisId="gold"
          domain={goldYAxisDomain}
          orientation="left"
          tick={{ fontSize: 12, fill: "hsl(var(--gold))" }}
          stroke="hsl(var(--gold))"
          tickLine={{ stroke: "hsl(var(--gold))" }}
          axisLine={{ stroke: "hsl(var(--gold))" }}
          tickMargin={12}
          tickCount={6}
          label={{ 
            value: 'Oro (€/g)', 
            angle: -90, 
            position: 'insideLeft', 
            offset: -5,
            style: { 
              textAnchor: 'middle', 
              fill: 'hsl(var(--gold))',
              fontSize: 14,
              fontWeight: 'bold'
            } 
          }}
          allowDecimals={true}
          tickFormatter={(value) => value.toFixed(1)}
        />
        <YAxis
          yAxisId="silver"
          domain={silverYAxisDomain}
          orientation="right"
          tick={{ fontSize: 12, fill: "hsl(var(--silver))" }}
          stroke="hsl(var(--silver))"
          tickLine={{ stroke: "hsl(var(--silver))" }}
          axisLine={{ stroke: "hsl(var(--silver))" }}
          tickMargin={12}
          tickCount={6}
          label={{ 
            value: 'Argento (€/g)', 
            angle: 90, 
            position: 'insideRight', 
            offset: -5,
            style: { 
              textAnchor: 'middle', 
              fill: 'hsl(var(--silver))',
              fontSize: 14,
              fontWeight: 'bold'
            } 
          }}
          allowDecimals={true}
          tickFormatter={(value) => value.toFixed(2)}
        />
        <Tooltip 
          formatter={customTooltipFormatter}
          labelFormatter={(label) => {
            // Handle case where label is just a year (for yearly data)
            if (typeof label === 'string' && /^\d{4}$/.test(label)) {
              return `Anno ${label}`;
            }
            
            // For 'all' period with non-year timestamps, try to extract the year
            if (selectedTimePeriod === 'all') {
              try {
                const date = new Date(label);
                if (!isNaN(date.getTime())) {
                  return `Anno ${date.getFullYear()}`;
                }
              } catch (e) {
                // Fall through to default handling
              }
            }
            
            const date = new Date(label);
            if (isNaN(date.getTime())) {
              // If we can't parse it as a date, just return the raw value
              return String(label);
            }
            
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
          }}
          contentStyle={{ 
            backgroundColor: 'hsl(var(--card))', 
            borderColor: 'hsl(var(--border))', 
            borderRadius: '0.75rem',
            boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
            padding: '12px 16px',
            fontWeight: 'medium'
          }}
          labelStyle={{ 
            color: 'hsl(var(--foreground))',
            fontWeight: 'bold',
            borderBottom: '1px solid hsl(var(--border))',
            marginBottom: '10px',
            paddingBottom: '6px'
          }}
          itemStyle={{ padding: '5px 0' }}
          wrapperStyle={{ zIndex: 10 }}
          cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '5 5' }}
        />
        <Legend 
          verticalAlign="top" 
          align="center"
          wrapperStyle={{ paddingBottom: '15px' }}
          iconType="circle"
          iconSize={10}
          formatter={(value) => (
            <span style={{ 
              color: value === 'gold' ? 'hsl(var(--gold))' : 'hsl(var(--silver))',
              fontWeight: 'bold',
              padding: '0 10px'
            }}>
              {value === 'gold' ? 'Oro' : 'Argento'}
            </span>
          )}
        />
        
        <ReferenceLine {...goldReferenceLineProps} />
        <ReferenceLine {...silverReferenceLineProps} />
        
        <Area
          yAxisId="gold"
          type="monotone"
          dataKey="gold"
          name="gold"
          stroke="hsl(var(--gold))"
          strokeWidth={2.5}
          fillOpacity={0.6}
          fill="url(#colorGold)"
          dot={false}
          activeDot={{ 
            r: 7, 
            strokeWidth: 2, 
            stroke: 'hsl(var(--background))',
            fill: 'hsl(var(--gold))'
          }}
          animationDuration={800}
          animationEasing="ease-in-out"
        />
        <Area
          yAxisId="silver"
          type="monotone"
          dataKey="silver"
          name="silver"
          stroke="hsl(var(--silver))"
          strokeWidth={2.5}
          fillOpacity={0.6}
          fill="url(#colorSilver)"
          dot={false}
          activeDot={{ 
            r: 7, 
            strokeWidth: 2, 
            stroke: 'hsl(var(--background))',
            fill: 'hsl(var(--silver))'
          }}
          animationDuration={800}
          animationEasing="ease-in-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
